import { Fragment, useEffect, useState, useRef } from "react";
import PageLayout from "../Layouts/PageLayout";
import Spinner from "../FieldComponets/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { PAGES } from "../../Constants/CommonConstants";
import { MaterialReactTable } from "material-react-table";
import { paginationQuery } from "../../utils/GlobalHelper";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import React from "react";
import { Link } from "react-router-dom";
import { SubscriptionPlanListAction } from "../../Redux/Action/SubscriptionPlanAction";
import { url } from "../../Config/Config";
import { encodeID } from "../../utils/Helper";

function SubscriptionPlansList() {
  const dispatch = useDispatch<any>();
  const listState = useSelector((state: any) => state.SubscriptionPlanListState);

  const [data, setData] = useState<any>([]);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<any>(["ASC"]);
  const formRef = useRef<HTMLFormElement>(null);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: PAGES?.pageIndex,
    pageSize: PAGES?.pageSize,
  });
  /** Sent to API; updated only on Search click or Enter (not on every keystroke). */
  const [searchValue, setSearchValue] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [rowCount, setRowCount] = useState(0);

  const applySearch = () => {
    setSearchValue(searchInput.trim());
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const columns: any = [
    {
      accessorKey: "S.NO",
      header: "S.No",
      accessorFn: (row: object, index: number) => {
        const currentPage = pagination.pageIndex + 1 || 1;
        const itemsPerPage = pagination.pageSize || 10;
        const startIndex = (currentPage - 1) * itemsPerPage;
        return startIndex + index + 1;
      },
      enableSorting: false,
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
      size: 50,
    },
    {
      accessorKey: "name",
      header: "Subscription name",
      enableSorting: false,
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: "price",
      header: "Subscription price",
      accessorFn: (row: { price: string | number }) =>
        row?.price != null ? Number(row.price).toFixed(2) : "—",
      enableSorting: false,
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: "validity_days",
      header: "Subscription validity",
      accessorFn: (row: { validity_days: number }) =>
        row?.validity_days != null ? `${row.validity_days} days` : "—",
      enableSorting: false,
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
    },
    {
      header: "Action",
      size: 120,
      accessorFn: (row: { id: number }) => (
        <div className="btn-group action-btn-table">
          <Link
            to={`${url}/subscription-plan-view/${encodeID(row?.id)}`}
            className="btn btn-info me-2"
            title="View"
          >
            <i className="fas fa-eye"></i>
          </Link>
          <Link
            to={`${url}/subscription-plan-edit/${encodeID(row?.id)}`}
            className="btn btn-primary me-2"
            title="Edit"
          >
            <i className="fas fa-edit"></i>
          </Link>
        </div>
      ),
      enableSorting: false,
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!data?.length) {
        setLoading(true);
      } else {
        setIsRefetching(true);
      }
      try {
        const query: any = paginationQuery(
          pagination.pageIndex,
          pagination.pageSize,
          sorting,
          searchValue,
          columnFilters,
        );
        await dispatch(SubscriptionPlanListAction(query));
      } catch {
        setIsError(true);
        return;
      }
      setIsError(false);
      setLoading(false);
      setIsRefetching(false);
    };
    fetchData();
  }, [
    columnFilters,
    searchValue,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
  ]);

  useEffect(() => {
    setData(listState?.data?.data?.rows);
    setRowCount(listState?.data?.data?.count);
  }, [listState?.data]);

  return (
    <Fragment>
      {(loading || listState?.loading) ? <Spinner /> : ""}

      <PageLayout
        title="Offline subscription plans"
        breadcrumbs={{
          secLast: { link: "/subscription-plans", name: "Subscription plans" },
        }}
        buttons={[{ url: "", className: "", text: "", show: false }]}
      >
        <form ref={formRef}>
          <div className="row gy-3 mb-3">
            <div className="col-md-6 col-lg-4 col-xl-3">
              <label>Search</label>
              <div className="input-group w-100">
                <span className="input-group-text" id="sub-search">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control m-0"
                  placeholder="Search by name or plan code"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applySearch();
                    }
                  }}
                  aria-label="Search subscription plans"
                />
              </div>
            </div>
          </div>
        </form>
        {data && (
          <MaterialReactTable
            columns={columns}
            data={data}
            enableGlobalFilter={false}
            enableHiding={false}
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableColumnActions={false}
            enableFullScreenToggle={false}
            positionToolbarAlertBanner="top"
            initialState={{ showColumnFilters: false }}
            manualFiltering
            manualPagination
            manualSorting
            muiToolbarAlertBannerProps={
              isError ? { color: "error", children: "Error loading data" } : undefined
            }
            onColumnFiltersChange={setColumnFilters}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            rowCount={rowCount}
            state={{
              columnFilters,
              pagination,
              showAlertBanner: isError,
              showProgressBars: isRefetching,
              sorting,
            }}
          />
        )}
      </PageLayout>
    </Fragment>
  );
}

export default SubscriptionPlansList;
